import { LoginOrigin } from '@/models/User';
import { ActivityMode, ContentType, ContentMode, PostMessage } from '@/models/Session';
import { LessonMode } from '@/models/Lesson';

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useSessionStorage } from '@/composables/useStorage';

export const useSessionStore = defineStore('session', () => {
  const storageKeys = [
    'contentType',
    'platform',
    'contentMode',
    'activityMode',
    'contentId',
    'lmsSessionId',
    'lessonMode',
    'lastContextId',
    'lastActivityId',
    'progressOTP',
  ];
  const storageHandlers = storageKeys.map((key) => useSessionStorage(key));

  const contentType = ref<ContentType>();
  const platform = ref<LoginOrigin>();
  const contentMode = ref<ContentMode>();
  const activityMode = ref<ActivityMode>(storageHandlers[3].get());
  const contentId = ref<number>();
  const lmsSessionId = ref<string>(storageHandlers[5].get());
  const lessonMode = ref<LessonMode>();
  const lastContextId = ref<string>(storageHandlers[7].get());
  const lastActivityId = ref<string>(storageHandlers[8].get());
  const progressOTP = ref<string>(storageHandlers[9].get());
  const layoutClass = ref<string>('');

  const isLms = computed(
    () =>
      platform.value &&
      [
        LoginOrigin.LTI.toLowerCase(),
        LoginOrigin.LTI13.toLowerCase(),
        LoginOrigin.RS.toLowerCase(),
      ].includes(platform.value.toLowerCase()),
  );
  const isNavigationVisible = computed(
    () =>
      isLms.value === false &&
      [ContentType.course, ContentType.lesson].includes(contentType.value as ContentType),
  );
  const isFooterVisible = computed(
    () =>
      isNavigationVisible.value &&
      (contentType.value !== ContentType.lesson || lessonMode.value === LessonMode.cherryPick),
  );
  const canCloseActivity = computed(() => activityMode.value === ActivityMode.normal);
  const canShareActivity = computed(
    () =>
      isLms.value === false && (canCloseActivity.value || activityMode.value === ActivityMode.bite),
  );

  const setContentType = (newContentType: ContentType) => {
    contentType.value = newContentType;
    storageHandlers[0].set(newContentType);
  };

  const setPlatform = (newPlatform: LoginOrigin) => {
    platform.value = newPlatform;
    storageHandlers[1].set(newPlatform);
  };

  const setContentMode = (newContentMode: ContentMode) => {
    contentMode.value = storageHandlers[2].get() ?? newContentMode;
    storageHandlers[2].set(contentMode.value);
  };

  const setActivityMode = (newActivityMode: ActivityMode) => {
    activityMode.value = newActivityMode;
    storageHandlers[3].set(activityMode.value);
  };

  const setContentId = (newContentId: number) => {
    contentId.value = isLms.value ? storageHandlers[4].get() ?? newContentId : newContentId;
    storageHandlers[4].set(contentId.value);
  };

  const setLmsSessionId = (newLmsSessionId: string) => {
    lmsSessionId.value = newLmsSessionId;
    storageHandlers[5].set(newLmsSessionId);
  };

  const setLessonMode = (newLessonMode: LessonMode) => {
    lessonMode.value = newLessonMode;
    storageHandlers[6].set(newLessonMode);
  };

  const setLastContextId = (newContextId: string) => {
    lastContextId.value = newContextId;
    storageHandlers[7].set(newContextId);
  };

  const setLastActivityId = (newActivityId: string) => {
    lastActivityId.value = newActivityId;
    storageHandlers[8].set(newActivityId);
  };

  const setProgressOTP = (newProgressOTP: string) => {
    progressOTP.value = newProgressOTP;
    storageHandlers[9].set(newProgressOTP);
  };

  const setLayoutClass = (newLayoutClass: string) => {
    layoutClass.value = newLayoutClass;
  };

  /**
   * This function will send a postMessage to the parent if the page is rendered inside an iframe
   * and activityMode is one of the required ones.
   * @param message
   */
  const sendPostMessage = (message: PostMessage): boolean => {
    if (
      !window.parent ||
      window.parent === window.self ||
      !activityMode.value ||
      ![ActivityMode.bite, ActivityMode.single].includes(activityMode.value)
    )
      return false;

    window.parent.postMessage(message, document.referrer);

    return true;
  };

  return {
    contentType,
    platform,
    contentMode,
    activityMode,
    contentId,
    lmsSessionId,
    lessonMode,
    lastContextId,
    lastActivityId,
    progressOTP,
    isLms,
    isNavigationVisible,
    isFooterVisible,
    canCloseActivity,
    canShareActivity,
    layoutClass,
    setLayoutClass,
    setContentType,
    setPlatform,
    setActivityMode,
    setContentMode,
    setContentId,
    setLmsSessionId,
    setLessonMode,
    setLastContextId,
    setLastActivityId,
    setProgressOTP,
    sendPostMessage,
  };
});
